import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { setLoggerContext } from '@/lib/logger';
import { identifyOrganization, identifyUser, resetAnalytics } from '@/lib/analytics';
import { useAppStore } from '@/store/appStore';

/**
 * Wires PostHog identity: who the person is, which account they're acting in,
 * and the super-property envelope every event inherits. Mounted once from App.
 *
 * To capture a product event, use `useTrack()` from `@/lib/analytics` instead.
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const { isLoaded, isSignedIn, user } = useUser();
  const { isLoaded: isOrgLoaded, organization } = useOrganization();
  const tenantId = useAppStore((state) => state.tenantDetails?.tenant?.id);

  // Person identity, deliberately NOT gated on the organization. Session
  // recording starts at page load, and PostHog labels a recording with the
  // person's `email` property — falling back to the raw distinct id when it
  // isn't set yet. Waiting for the org to resolve (or for a user who has no
  // active org at all: sign-in, onboarding, mid-switch) left that window
  // showing a Clerk id instead of an email. Identify as soon as Clerk has
  // the user; the org catches up on its own below.
  useEffect(() => {
    if (!posthog || !isLoaded || !isSignedIn || !user) return;

    identifyUser(posthog, user);

    // Global context merged into every error event.
    setLoggerContext({
      email: user.primaryEmailAddress?.emailAddress,
      username: user.username,
    });
  }, [posthog, isLoaded, isSignedIn, user]);

  // Account context — the organization group plus the super-property envelope.
  useEffect(() => {
    if (!posthog || !isOrgLoaded || !organization) return;

    identifyOrganization(posthog, organization, tenantId);
    setLoggerContext({
      organizationId: organization.id,
      organizationName: organization.name,
    });
  }, [posthog, isOrgLoaded, organization, tenantId]);

  const reset = () => resetAnalytics(posthog);

  return { reset };
}
